import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Chip,
} from "@mui/material";
import { EmojiEvents } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useGetWeeklyLeaderboardQuery } from "../../../../../app/leaderboardApiSlice";
import UserAvatar from "../../../../common/UserAvatar";

const MEDAL_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"];

const getDisplayName = (user) => {
  const p = user?.auth_profile;
  if (p?.first_name || p?.last_name)
    return `${p.first_name || ""} ${p.last_name || ""}`.trim();
  return user?.username || user?.schoola_id || "—";
};

const getInitials = (user) => {
  const name = getDisplayName(user);
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
};

const StudentLeaderboard = () => {
  const authData = useSelector((state) => state.auth);
  const userId = authData?.user?.id;

  const { data, isLoading } = useGetWeeklyLeaderboardQuery();

  const entries = React.useMemo(() => {
    if (!data) return [];
    // data is now paginated: { items, total, page, pages, has_next }
    const list = Array.isArray(data) ? data : (data.items || []);
    return [...list].sort((a, b) => (b.points || 0) - (a.points || 0));
  }, [data]);

  const weekLabel = entries[0]?.name || "";

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
        <EmojiEvents sx={{ color: "#F59E0B", fontSize: "2rem" }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            Weekly Leaderboard
          </Typography>
          {weekLabel && (
            <Typography variant="caption" color="text.secondary">
              Week {weekLabel}
            </Typography>
          )}
        </Box>
      </Box>

      {isLoading ? (
        <Box>
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : entries.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <EmojiEvents sx={{ fontSize: "4rem", color: "#e0e0e0", mb: 1 }} />
          <Typography color="text.secondary">No entries yet this week. Take a quiz to get on the board!</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#FAFAFA" }}>
                <TableCell sx={{ fontWeight: 700, width: 60 }}>Rank</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Points</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Questions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry, idx) => {
                const rank = idx + 1;
                const isMe = entry.user_id === userId;
                return (
                  <TableRow
                    key={entry.id}
                    sx={{
                      bgcolor: isMe ? (theme) => theme.palette.mode === "dark" ? "#2e2a1a" : "#FFF8E1" : "inherit",
                      "&:hover": { bgcolor: isMe ? (theme) => theme.palette.mode === "dark" ? "#3a3010" : "#FFF3CD" : (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9" },
                    }}
                  >
                    <TableCell>
                      {rank <= 3 ? (
                        <EmojiEvents sx={{ color: MEDAL_COLORS[rank - 1], fontSize: "1.4rem", verticalAlign: "middle" }} />
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                          #{rank}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <UserAvatar
                          src={entry.user?.auth_profile?.photo || undefined}
                          name={getDisplayName(entry.user)}
                          sx={{ width: 34, height: 34, fontSize: "0.85rem" }}
                        />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: isMe ? 700 : 500 }}>
                            {getDisplayName(entry.user)}
                            {isMe && <Chip label="You" size="small" sx={{ ml: 1, height: 18, fontSize: "0.65rem", bgcolor: "#F59E0B", color: "#fff" }} />}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{(entry.points || 0).toFixed(1)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">{entry.questions || 0}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default StudentLeaderboard;
